/**
 * OrderStatus enum — synced with backend numeric status_id values.
 * Backend is the single source of truth. Numeric values must match exactly for API serialization.
 *
 * Backend contract (status_id / status_code / status_name):
 *   1 Confirmed / OrderCreated  → customer label "Order received" (this enum: Pending)
 *   2 Accepted / RestaurantAccepted → customer label "Restaurant accepted" (this enum: Confirmed)
 *
 * Enum member names are kept for analytics/call-site stability. Do not renumber.
 */
export enum OrderStatus {
  Unknown = 0,
  /** status_id 1 — backend Confirmed / OrderCreated */
  Pending = 1,
  /** status_id 2 — backend Accepted / RestaurantAccepted */
  Confirmed = 2,
  Preparing = 3,
  Ready = 4,
  Packaging = 5,
  ReadyToShip = 6,
  Shipped = 7,
  OutForDelivery = 8,
  Delivered = 9,
  Cancelled = 10,
  Returned = 11,
  Refunded = 12,
  Completed = 13,
}

export const OrderStatusLabel: Record<OrderStatus, string> = {
  [OrderStatus.Unknown]: "Unknown",
  [OrderStatus.Pending]: "Order received",
  [OrderStatus.Confirmed]: "Restaurant accepted",
  [OrderStatus.Preparing]: "Preparing",
  [OrderStatus.Ready]: "Ready",
  [OrderStatus.Packaging]: "Packaging",
  [OrderStatus.ReadyToShip]: "Ready for dispatch",
  [OrderStatus.Shipped]: "Shipped",
  [OrderStatus.OutForDelivery]: "Out for delivery",
  [OrderStatus.Delivered]: "Delivered",
  [OrderStatus.Cancelled]: "Cancelled",
  [OrderStatus.Returned]: "Returned",
  [OrderStatus.Refunded]: "Refunded",
  [OrderStatus.Completed]: "Completed",
};

/** Backend status_code / status_name aliases → numeric enum. Keys are lowercase, spaces stripped. */
const STATUS_NAME_ALIASES: Record<string, OrderStatus> = {
  unknown: OrderStatus.Unknown,
  pending: OrderStatus.Pending,
  confirmed: OrderStatus.Pending,
  ordercreated: OrderStatus.Pending,
  accepted: OrderStatus.Confirmed,
  restaurantaccepted: OrderStatus.Confirmed,
  preparing: OrderStatus.Preparing,
  ready: OrderStatus.Ready,
  packaging: OrderStatus.Packaging,
  readytoship: OrderStatus.ReadyToShip,
  shipped: OrderStatus.Shipped,
  outfordelivery: OrderStatus.OutForDelivery,
  delivered: OrderStatus.Delivered,
  cancelled: OrderStatus.Cancelled,
  returned: OrderStatus.Returned,
  refunded: OrderStatus.Refunded,
  completed: OrderStatus.Completed,
}

function parseOrderStatusName(value: string): OrderStatus | undefined {
  const key = value.trim().replace(/\s+/g, "").toLowerCase()
  if (!key) return undefined
  return STATUS_NAME_ALIASES[key]
}

/** Parse API value (number, string, or status object) to OrderStatus. Returns Unknown for invalid values. */
export function parseOrderStatus(value: unknown): OrderStatus {
  if (typeof value === "number" && Number.isInteger(value)) {
    if (value >= OrderStatus.Unknown && value <= OrderStatus.Completed) {
      return value as OrderStatus;
    }
    return OrderStatus.Unknown;
  }
  if (typeof value === "string") {
    const normalized = value.trim();
    const byName = parseOrderStatusName(normalized);
    if (byName !== undefined) return byName;
    const num = parseInt(normalized, 10);
    if (!Number.isNaN(num) && num >= 0 && num <= 13) return num as OrderStatus;
  }
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    const block = value as Record<string, unknown>;
    // Priority mirrors payment-timing object unwrap, plus backend aliases.
    return parseOrderStatus(
      block.code ??
        block.status_id ??
        block.value ??
        block.status_code ??
        block.name ??
        block.status_name ??
        block.id
    );
  }
  return OrderStatus.Unknown;
}
